import { JSMol } from "@/types/rdkit";

/**
 * Wraps a V3000 CTAB block into a valid standalone V3000 molfile.
 */
function ctabToMolblock(ctabLines: string[]): string {
    return [
        "",                                          // molecule name (blank)
        "     RDKit          3D",                   // program/timestamp line
        "",                                          // comment line
        "  0  0  0  0  0  0  0  0  0  0999 V3000", // counts line (V3000 marker)
        ...ctabLines,                                // the CTAB block itself
        "M  END",
    ].join("\n");
}

/**
 * Extracts the V3000 CTAB lines from a molfile or SDF record.
 * Returns null if no CTAB block is found.
 */
function extractCtabLines(text: string): string[] | null {
    const lines = text.split("\n");
    const ctabLines: string[] = [];
    let inside = false;

    for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed === "M  V30 BEGIN CTAB") {
            ctabLines.push(line);
            inside = true;
        } else if (trimmed === "M  V30 END CTAB") {
            ctabLines.push(line);
            inside = false;
        } else if (inside) {
            ctabLines.push(line);
        }
    }

    return ctabLines.length > 0 ? ctabLines : null;
}

/**
 * Parses a V3000 RXN file and returns molecules grouped by role.
 * Each CTAB block is extracted and wrapped into a valid V3000 molfile.
 */
function parseRxnFileRoles(rxnFileText: string, RDKitModule): { reactants: JSMol[]; products: JSMol[]; reagents: JSMol[] } {
    const lines = rxnFileText.split("\n");

    const roles = { reactants: [], reagents: [], products: [] };
    let currentRole = null;
    let currentCtab = null;
    let insideCtab = false;

    for (const line of lines) {
        const trimmed = line.trim();

        if (trimmed === "M  V30 BEGIN REACTANT") {
            currentRole = "reactants";
            continue;
        } else if (trimmed === "M  V30 BEGIN AGENT") {
            currentRole = "reagents";
            continue;
        } else if (trimmed === "M  V30 BEGIN PRODUCT") {
            currentRole = "products";
            continue;
        } else if (
            trimmed === "M  V30 END REACTANT" ||
            trimmed === "M  V30 END AGENT" ||
            trimmed === "M  V30 END PRODUCT"
        ) {
            currentRole = null;
            continue;
        }

        if (currentRole) {
            if (trimmed === "M  V30 BEGIN CTAB") {
                currentCtab = [line];
                insideCtab = true;
            } else if (trimmed === "M  V30 END CTAB") {
                currentCtab.push(line);
                roles[currentRole].push(currentCtab);
                currentCtab = null;
                insideCtab = false;
            } else if (insideCtab) {
                currentCtab.push(line);
            }
        }
    }

    const result: { reactants: JSMol[]; products: JSMol[]; reagents: JSMol[] } = { reactants: [], products: [], reagents: [] };
    for (const [role, ctabs] of Object.entries(roles)) {
        result[role] = ctabs.map((ctabLines) => {
            const molblock = ctabToMolblock(ctabLines);
            const mol = RDKitModule.get_mol(molblock);
            if (!mol || !mol.is_valid()) {
                console.warn(`Failed to parse a ${role} molecule`);
                return null;
            }
            return mol;
        }).filter(Boolean);
    }

    return result;
}


/**
 * Parses a V3000 SDF file and returns each molecule as a JSMol.
 * Records are delimited by "$$$$" lines. Each record's CTAB block is
 * extracted and wrapped into a valid V3000 molfile before parsing.
 */
function parseSdfFile(sdfText: string, RDKitModule): JSMol[] {
    const records = sdfText.split(/\$\$\$\$\r?\n?/);

    return records
        .map((record) => record.trim())
        .filter((record) => record.length > 0)
        .map((record, i) => {
            const ctabLines = extractCtabLines(record);
            if (!ctabLines) {
                console.warn(`SDF record ${i} contains no CTAB block`);
                return null;
            }
            const molblock = ctabToMolblock(ctabLines);
            const mol = RDKitModule.get_mol(molblock);
            if (!mol || !mol.is_valid()) {
                console.warn(`Failed to parse SDF record ${i}`);
                return null;
            }
            return mol;
        })
        .filter(Boolean) as JSMol[];
}

export { parseRxnFileRoles, parseSdfFile };